import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  SaveClosurePayload,
  SaveSchedulePayload,
} from "@/domain/site-office-hours.interface";
import { siteOfficeHoursService } from "@/services/siteOfficeHours.service";

export const useGetSiteOfficeHoursSchedule = ({
  programId,
  orgUnitId,
  enabled = true,
}: {
  programId: string;
  orgUnitId: string;
  enabled?: boolean;
}) => {
  const {
    data: schedule,
    isLoading: isLoadingSchedule,
    isFetching: isFetchingSchedule,
    refetch: refetchSchedule,
  } = useQuery({
    queryKey: ["site-office-hours-schedule", programId, orgUnitId],
    queryFn: () => siteOfficeHoursService.getSchedule(programId, orgUnitId),
    enabled: enabled && !!programId && !!orgUnitId,
  });

  return { schedule, isLoadingSchedule, isFetchingSchedule, refetchSchedule };
};

export const useGetSiteOfficeClosure = ({
  programId,
  orgUnitId,
  enabled = true,
}: {
  programId: string;
  orgUnitId: string;
  enabled?: boolean;
}) => {
  const {
    data: closure,
    isLoading: isLoadingClosure,
    isFetching: isFetchingClosure,
    refetch: refetchClosure,
  } = useQuery({
    queryKey: ["site-office-hours-closure", programId, orgUnitId],
    queryFn: () =>
      siteOfficeHoursService.getActiveClosure(programId, orgUnitId),
    enabled: enabled && !!programId && !!orgUnitId,
  });

  return { closure, isLoadingClosure, isFetchingClosure, refetchClosure };
};

export const useSaveSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveSchedulePayload) =>
      siteOfficeHoursService.saveSchedule(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "site-office-hours-schedule",
          variables.programId,
          variables.orgUnitId,
        ],
      });
    },
  });
};

export const useSaveClosure = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveClosurePayload) =>
      siteOfficeHoursService.saveClosure(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "site-office-hours-closure",
          variables.programId,
          variables.orgUnitId,
        ],
      });
    },
  });
};
